import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
  type PaginationState,
} from "@tanstack/react-table";
import classNames from "classnames";
import type { Dispatch, SetStateAction } from "react";
import GridLoader from "react-spinners/GridLoader";

interface TableProps {
  isSearchable?: boolean;
  isSortable?: boolean;
  pagination?: boolean;
  paginationState?: PaginationState;
  setPaginationState?: Dispatch<SetStateAction<PaginationState>>;
  isExpandable?: boolean;
  columns: any[];
  data: any[];
  tableClass?: string;
  trClass?: string;
  tdClass?: string;
  thClass?: string;
  theadRowClass?: string;
  isFetching?: boolean;
  getRowClassName?: (row: any) => string | undefined;
}

const Table = ({
  isSearchable = false,
  isSortable = false,
  pagination = false,
  paginationState,
  setPaginationState,
  isExpandable = false,
  columns,
  data,
  tableClass,
  theadRowClass,
  trClass,
  tdClass,
  thClass,
  isFetching = false,
  getRowClassName,
}: TableProps) => {
  const table = useReactTable({
    data,
    columns,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isSearchable ? getFilteredRowModel() : undefined,
    getSortedRowModel: isSortable ? getSortedRowModel() : undefined,
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: isExpandable ? getExpandedRowModel() : undefined,

    // Controlled pagination is owned by the parent; avoid auto-resets that
    // call onPaginationChange every data change and can freeze the UI.
    autoResetPageIndex: false,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
  });

  const rows = pagination
    ? table.getPaginationRowModel().rows
    : table.getRowModel().rows;

  return (
    <table
      className={`${tableClass ? tableClass : `table table-centered table-responsive rounded mt-3 ${isFetching ? "" : "border"}`}`
      }
    >
      <thead className={`${theadRowClass ? theadRowClass : 'bg-gray-100 border-b border-gray-200'}`}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const columnDef = header.column.columnDef as any;
              const align = columnDef.meta?.align || columnDef.align || 'left';
              return (
                <th
                  key={header.id}
                  className={classNames(
                    thClass || 'text-gray-700 font-semibold text-sm py-4 px-6 whitespace-nowrap',
                    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left',
                    {
                      sorting_asc: header.column.getIsSorted() === "asc",
                      sorting_desc: header.column.getIsSorted() === "desc",
                      sortable: isSortable,
                    }
                  )}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ 
                    cursor: isSortable ? "pointer" : undefined,
                    width: columnDef.size ? `${columnDef.size}px` : undefined,
                    minWidth: columnDef.minSize
                      ? `${columnDef.minSize}px`
                      : columnDef.size
                        ? `${columnDef.size}px`
                        : undefined,
                    maxWidth: columnDef.maxSize ? `${columnDef.maxSize}px` : undefined,
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {isFetching && rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length}>
              <div className="flex justify-center items-center h-64">
                <GridLoader color="#dbb586" />
              </div>
            </td>
          </tr>
        ) : (
          <>
            {rows.map((row, index) => {
              const rowClassName = getRowClassName?.(row.original);
              return (
              <tr
                className={classNames(
                  trClass || '',
                  !trClass && (index % 2 === 1 ? 'bg-gray-50' : 'bg-white'),
                  !trClass && (index === rows.length - 1 ? 'border-none' : 'border-b border-gray-200'),
                  !trClass && 'hover:bg-gray-50 transition-colors',
                  trClass && !rowClassName && 'bg-white transition-colors',
                  rowClassName,
                )}
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => {
                  const columnDef = cell.column.columnDef as any;
                  const align = columnDef.meta?.align || columnDef.align || 'left';
                  return (
                    <td 
                      key={cell.id} 
                      className={classNames(
                        tdClass || '',
                        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
                      )}
                      style={{
                        width: columnDef.size ? `${columnDef.size}px` : undefined,
                        minWidth: columnDef.minSize
                          ? `${columnDef.minSize}px`
                          : columnDef.size
                            ? `${columnDef.size}px`
                            : undefined,
                        maxWidth: columnDef.maxSize ? `${columnDef.maxSize}px` : undefined,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
            })}
          </>
        )}
      </tbody>
    </table>
  );
};

export default Table;
